import styled, { css } from 'styled-components';

import {
  getGridBase,
  getThemeColor,
  getThemeColorWithError,
  getThemePath,
  getThemeTransition,
  media,
} from '@utils/styled';

import Button, { BUTTON_VARIANTS } from '@common/components/Button';
import JobOfferInfo from '@common/components/JobOfferInfo';
import { Section } from '@common/components/JobOfferInfo/JobOfferInfo.styled';
import Pill from '@common/components/Pill';
import Typography, { Text } from '@common/components/Typography';
import GridSectionContent from '@common/components/GridSectionContent';
import { SIZES, VARIANTS } from '@common/constants/pills';

export const countStates = {
  stickyVisible: 'stickyVisible',
  sitckyHidden: 'stickyHidden',
  static: 'static',
};

const stickyMixin = theme => css`
  position: fixed;
  max-width: ${theme.breakpoints[0]}rem;
  margin: 0 auto;
  ${theme.getFluidSpacing(
    'padding-left',
    'widthBased',
    0,
    false,
    'safe-area-inset-left',
  )}
  ${theme.getFluidSpacing(
    'padding-right',
    'widthBased',
    0,
    false,
    'safe-area-inset-right',
  )}
`;

export const CountInfo = styled.div`
  ${getGridBase()};
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 10;
  will-change: transform;
  transition: transform 300ms ease;

  ${({ state, theme }) => {
    switch (state) {
      case countStates.stickyVisible:
        return css`
          ${stickyMixin(theme)}
          transform: translateY(0);
        `;
      case countStates.stickyHidden:
        return css`
          ${stickyMixin(theme)}
          transform: translateY(100%);
        `;
      case countStates.static:
        return css`
          transform: translateY(0);
        `;
      default:
        return null;
    }
  }}

  ${GridSectionContent} {
    display: flex;
    align-items: center;
    border-top: 1px solid ${getThemeColor('bluegrey.80')};
    padding: 2.3rem env(safe-area-inset-right)
      calc(2.3rem + env(safe-area-inset-bottom));
    background-color: ${getThemeColor('grey.100')};
  }
`;

export const CountInfoWrapper = styled.div`
  display: ${({ isHidden }) => (isHidden ? 'none' : 'block')};
  height: calc(${({ height }) => height}px + env(safe-area-inset-bottom));
  ${({ theme }) => theme.getFluidSpacing('margin-top', 'widthBased', 1)};
`;

export const CountLabel = styled(Typography).attrs({
  customAs: 'span',
  variant: 'paragraph2',
})`
  margin: 0;
  margin-right: 0.5rem;
  font-weight: 500;
`;

export const CountPill = styled(Pill).attrs({
  size: SIZES.SMALL,
  variant: VARIANTS.LIGHT,
})`
  position: relative;
  top: -1px;
  display: inline-flex;
  width: 1.8rem;
  height: 1.8rem;
  padding: 0 1px 0 0;
`;

export const BackgroundGradient = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 20rem;
  background: linear-gradient(
    -180deg,
    rgb(255 255 255 / 0%) 0%,
    rgb(255 255 255 / 60%) 33%,
    rgb(255 255 255) 90%
  );

  &::after {
    content: '';
    position: absolute;
    bottom: 2.5rem;
    left: 0;
    z-index: 2;
    width: 100%;
    height: 1px;
    background-color: ${getThemeColor('bluegrey.90')};
  }
`;

export const AlternativesSection = styled.div`
  position: relative;
  overflow: hidden;
  max-height: 0;
  transition: ${getThemeTransition('max-height', 'slow', 'easeIn')};

  &.transition-enter {
    overflow: hidden;
    max-height: 0;
  }

  &.transition-enter-active {
    overflow: hidden;
    max-height: 45rem;
  }

  &.transition-enter-done {
    overflow: visible;
    max-height: none;
  }

  &.transition-exit {
    overflow: hidden;
    max-height: 90vh;
  }

  &.transition-exit-done,
  &.transition-exit-active {
    overflow: hidden;
    max-height: 0;
  }
`;

export const AlternativesList = styled.div`
  position: relative;
  overflow: hidden;
  max-height: ${({ maxHeight }) => maxHeight};
  padding: 0.3rem;
  ${getGridBase()}
  ${({ theme }) => theme.getFluidSpacing('grid-row-gap', 'scale', 15)};

  ${media.from800up`
    ${({ theme }) => theme.getFluidSpacing('grid-gap', 'scale', 15)};
  `}
`;

export const AlternativesListWrapper = styled.div``;

export const JobOffersItem = styled(JobOfferInfo)`
  display: flex;
  align-items: stretch;
  grid-column: span 12;
  margin: -1px;
  outline: 0.2rem solid transparent;
  ${({ selected }) =>
    selected &&
    css`
      outline-color: ${getThemeColor('hue.blue')};
    `};
  outline-offset: -2px;
  border: solid ${getThemeColorWithError('bluegrey.70', 'hue.blue', 'selected')};
  border-width: 1px;
  border-radius: ${getThemePath(['borderRadiuses', 'normal.2'])}rem;
  ${({ theme }) => theme.getFluidSpacing('padding-top', 'scale', 3)};
  ${({ theme }) => theme.getFluidSpacing('padding-right', 'widthBased', 2)};
  ${({ theme }) => theme.getFluidSpacing('padding-bottom', 'scale', 3)};
  ${({ theme }) => theme.getFluidSpacing('padding-left', 'widthBased', 2)};
  background-color: ${getThemeColor('grey.100')};
  text-align: left;
  transition:
    ${getThemeTransition('background')}, ${getThemeTransition('border-color')};
  cursor: pointer;

  ${/* sc-sel */ Section}:last-child {
    padding-bottom: 0;
  }

  &:hover {
    border-color: ${getThemeColor('bluegrey.60')};
    background-color: ${getThemeColor('bluegrey.97')};
    transition: background-color 1s ease;
  }

  &:focus {
    background-color: ${getThemeColor('bluegrey.97')};
  }

  &:focus-visible {
    border-color: ${getThemeColorWithError(
      'bluegrey.80',
      'hue.blue',
      'selected',
    )};
    background-color: ${getThemeColorWithError(
      'bluegrey.90',
      'bluegrey.95',
      'selected',
    )};
  }

  & ${/* sc-sel */ Text} {
    transition: ${getThemeTransition('color')};
  }

  &:disabled ${/* sc-sel */ Text} {
    color: ${getThemeColor('bluegrey.70')};
  }

  &:disabled {
    background-color: ${getThemeColorWithError(
      'bluegrey.95',
      'grey.100',
      'selected',
    )};
    cursor: not-allowed;
  }

  &:disabled:hover {
    border: solid
      ${getThemeColorWithError('bluegrey.70', 'hue.blue', 'selected')};
    border-width: ${({ selected }) => (selected ? 2 : 1)}px;
  }

  ${media.from800up`
    grid-column: span 6;
  `}

  ${media.from1040up`
    grid-column: span 4;
  `}
`;

export const ShowAllButton = styled(Button).attrs({
  type: 'button',
  variant: BUTTON_VARIANTS.LEVEL_3_GREY_100,
})`
  position: absolute;
  bottom: 0;
  z-index: 4;

  &::after {
    content: '';
    position: absolute;
    top: 45%;
    right: calc(100% + 2px);
    bottom: 45%;
    z-index: 3;
    width: 2rem;
    background-color: ${getThemeColor('grey.100')};
  }

  &::before {
    content: '';
    position: absolute;
    top: 45%;
    bottom: 45%;
    left: calc(100% + 2px);
    z-index: 3;
    width: 2rem;
    background-color: ${getThemeColor('grey.100')};
  }
`;
